import React, { useMemo, useState } from 'react';
import { Check, Clock } from 'lucide-react';
import { useAmbiance } from '../Ambiance/AmbianceContext';
import { Eyebrow, Pill } from './VitrineUi';
import { useDemoButton } from './VitrineLayout';
import { CARTES_MODULES, FAMILLES, FORMULES_VITRINE, LIGNES_FORMULES } from './vitrineAccueil';
import type { Famille } from './vitrineAccueil';
import { PILOTE } from './vitrineMetiers';

export const TITRE_SECTION = 'text-[30px] md:text-[38px] font-semibold leading-[1.08] tracking-[-0.02em]';

/** Cartes-modules : on filtre par famille, on touche une carte pour l'allumer. */
export const GrilleModules: React.FC = () => {
    const { tokens } = useAmbiance();
    const [famille, setFamille] = useState<Famille | 'all'>('all');
    const [allumes, setAllumes] = useState<Set<string>>(() => new Set(['Livre de réservations', 'Planning', 'Facture']));
    const cartes = useMemo(() => CARTES_MODULES.filter((c) => famille === 'all' || c.famille === famille), [famille]);
    const nbDisponibles = CARTES_MODULES.filter((c) => !c.bientot).length;
    const basculer = (nom: string) => setAllumes((prev) => {
        const next = new Set(prev);
        if (next.has(nom)) next.delete(nom); else next.add(nom);
        return next;
    });
    return (
        <section id="modules" className="max-w-[1160px] mx-auto px-5 py-16 md:py-20 scroll-mt-32">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
                <div className="max-w-[560px]">
                    <Eyebrow>Modulable</Eyebrow>
                    <h2 className={`${TITRE_SECTION} mb-3`}>Composez votre espace, carte par carte.</h2>
                    <p className="text-[14.5px] text-content-muted">{nbDisponibles} modules disponibles. Touchez une carte pour l'allumer : votre espace ne montre que ceux-là, pas d'écran inutile.</p>
                </div>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrer par famille">
                    {(['all', ...FAMILLES] as const).map((f) => (
                        <button
                            key={f}
                            type="button"
                            aria-pressed={famille === f}
                            onClick={() => setFamille(f)}
                            className={`h-[40px] px-4 rounded-full text-[13px] font-semibold whitespace-nowrap border transition-colors ${famille === f ? 'bg-content text-surface border-content' : 'bg-surface text-content border-border hover:border-content'}`}
                        >
                            {f === 'all' ? 'Tout voir' : f}
                        </button>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {cartes.map((c) => {
                    const on = allumes.has(c.name) && !c.bientot;
                    return (
                        <button
                            key={c.name}
                            type="button"
                            disabled={c.bientot}
                            aria-pressed={c.bientot ? undefined : on}
                            onClick={() => basculer(c.name)}
                            className={`carte-vivante text-left rounded-xl border p-4 min-h-[128px] flex flex-col ${c.bientot ? 'border-dashed border-border opacity-60 cursor-not-allowed' : on ? 'bg-surface-soft' : 'bg-surface border-border hover:border-content'}`}
                            style={on ? { borderColor: tokens.acc } : undefined}
                        >
                            <span className="flex items-center justify-between gap-2 mb-2">
                                <span className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted">{c.famille}</span>
                                {c.bientot
                                    ? <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[.09em] text-content-muted"><Clock size={11} aria-hidden="true" />Bientôt</span>
                                    : <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[.09em]" style={{ color: on ? tokens.acc : 'var(--text-muted)' }}>
                                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: on ? tokens.acc : 'var(--text-muted)' }} />{on ? 'Allumé' : 'Éteint'}
                                    </span>}
                            </span>
                            <span className="text-[15px] font-semibold leading-tight mb-1">{c.name}</span>
                            <span className="text-[12.5px] text-content-muted leading-snug">{c.desc}</span>
                        </button>
                    );
                })}
            </div>
            <p className="mt-5 text-[13.5px] text-content-muted" aria-live="polite">
                <strong className="text-content">{allumes.size}</strong> {allumes.size > 1 ? 'modules allumés' : 'module allumé'} dans votre espace.
            </p>
        </section>
    );
};

/** Les trois formules et leur tableau comparatif. */
export const BlocFormules: React.FC = () => {
    const { tokens } = useAmbiance();
    return (
        <section id="formules" className="bg-surface-app border-y border-border scroll-mt-32">
            <div className="max-w-[1160px] mx-auto px-5 py-16 md:py-20">
                <Eyebrow>Formules</Eyebrow>
                <h2 className={`max-w-[620px] ${TITRE_SECTION} mb-3`}>Trois formules, un prix par mois, sans commission sur vos réservations.</h2>
                <p className="max-w-[560px] text-[14.5px] text-content-muted mb-10">Vous pouvez aussi partir de la formule qui vous ressemble et ajouter des modules à la carte. Planning et équipe : gratuits jusqu'à 10 salariés, puis des paliers.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                    {FORMULES_VITRINE.map((f) => (
                        <div key={f.nom} className="rounded-2xl border bg-surface p-6 relative" style={{ borderColor: f.vedette ? tokens.acc : 'var(--border)', borderWidth: f.vedette ? 2 : 1 }}>
                            {f.vedette && <span className="absolute -top-3 left-6 rounded-full px-3 py-0.5 text-[11px] font-bold text-white" style={{ background: tokens.acc }}>La plus choisie</span>}
                            <div className="text-[15px] font-semibold">{f.nom}</div>
                            <div className="mt-2 mb-2"><span className="text-[40px] font-bold leading-none tracking-tight">{f.prix} €</span> <span className="text-[13px] text-content-muted">/ mois</span></div>
                            <p className="text-[13.5px] text-content-muted">{f.pour}</p>
                        </div>
                    ))}
                </div>
                <div className="rounded-2xl border border-border bg-surface overflow-x-auto">
                    <table className="w-full min-w-[560px] text-left text-[13.5px]">
                        <caption className="sr-only">Ce que comprend chaque formule</caption>
                        <thead>
                            <tr className="border-b border-border">
                                <th scope="col" className="py-3 px-4 font-semibold">Ce qui est inclus</th>
                                {FORMULES_VITRINE.map((f) => <th scope="col" key={f.nom} className="py-3 px-3 text-center font-semibold w-[110px]">{f.nom}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {LIGNES_FORMULES.map((l) => (
                                <tr key={l.label} className="border-b border-border last:border-0">
                                    <th scope="row" className="py-3 px-4 font-medium">
                                        {l.label}
                                        {l.bientot && <span className="ml-2 text-[10px] font-bold uppercase tracking-[.09em] text-content-muted">Bientôt</span>}
                                    </th>
                                    {l.valeurs.map((v, i) => (
                                        <td key={i} className="py-3 px-3 text-center">
                                            {v === true ? <Check size={17} className="inline" style={{ color: tokens.acc }} aria-label="Inclus" />
                                                : v === false ? <span className="text-content-muted" aria-label="Non inclus">—</span>
                                                    : <span className="font-semibold">{v}</span>}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className="mt-4 text-[12.5px] text-content-muted">Les modules marqués « Bientôt » sont en construction et ne sont pas encore disponibles.</p>
            </div>
        </section>
    );
};

/** Le programme « client pilote » : explication et demande (via le rendez-vous de 30 minutes). */
export const BlocPilote: React.FC = () => {
    const { tokens } = useAmbiance();
    const demoButton = useDemoButton();
    return (
        <section id="pilote" className="max-w-[1160px] mx-auto px-5 py-16 md:py-20 scroll-mt-32">
            <div className="rounded-2xl border-2 bg-surface p-7 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-10" style={{ borderColor: tokens.acc }}>
                <div>
                    <Eyebrow>Client pilote</Eyebrow>
                    <h2 className={`${TITRE_SECTION} mb-4`}>{PILOTE.titre}</h2>
                    <p className="text-[14.5px] text-content-muted leading-relaxed mb-6">{PILOTE.intro}</p>
                    <div className="flex flex-wrap items-center gap-3">
                        {demoButton('lg', PILOTE.demande) ?? <Pill size="lg" href="mailto:contact@l-iamani.com?subject=Demande%20client%20pilote">{PILOTE.demande}</Pill>}
                    </div>
                    <p className="mt-3 text-[12.5px] text-content-muted">{PILOTE.precision}</p>
                </div>
                <div className="space-y-6 self-center">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[.09em] mb-2" style={{ color: tokens.acc }}>Ce que vous y gagnez</div>
                        <ul className="space-y-2 text-[14px]">
                            {PILOTE.avantages.map((t) => <li key={t} className="flex gap-2"><Check size={16} className="mt-0.5 shrink-0" style={{ color: tokens.acc }} aria-hidden="true" />{t}</li>)}
                        </ul>
                    </div>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted mb-2">Ce qu'on vous demande</div>
                        <ul className="space-y-2 text-[14px] text-content-muted">
                            {PILOTE.contreparties.map((t) => <li key={t} className="flex gap-2"><span aria-hidden="true">·</span>{t}</li>)}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
};
